import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Template {
  id: string
  name: string
  subject: string
  category: string
  status: string
  variables: string[]
}

interface Campaign {
  id: string
  name: string
  template_id: string
  status: string
  audience_size: number
  sent: number
  opened: number
  clicked: number
  scheduled_at: string
}

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'templates' | 'campaigns'>('templates')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [tplRes, campRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/email-templates/templates'),
      fetch(API_BASE + '/email-templates/campaigns'),
      fetch(API_BASE + '/email-templates/statistics')
    ])
    if (tplRes.ok) setTemplates((await tplRes.json()).templates || [])
    if (campRes.ok) setCampaigns((await campRes.json()).campaigns || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'completed': return 'bg-green-100 text-green-800'
      case 'scheduled': return 'bg-blue-100 text-blue-800'
      case 'draft': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Email Templates & Campaigns</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Templates</div>
              <div className="text-2xl font-bold">{statistics.total_templates}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_templates}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Campaigns</div>
              <div className="text-2xl font-bold">{statistics.total_campaigns}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Emails Sent</div>
              <div className="text-2xl font-bold">{statistics.emails_sent?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Rate</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.open_rate}%</div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mb-6">
          <button onClick={() => setActiveTab('templates')} className={`px-4 py-2 rounded-lg ${activeTab === 'templates' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Templates</button>
          <button onClick={() => setActiveTab('campaigns')} className={`px-4 py-2 rounded-lg ${activeTab === 'campaigns' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Campaigns</button>
        </div>

        {activeTab === 'templates' ? (
          <div className="grid grid-cols-3 gap-6">
            {templates.map(tpl => (
              <div key={tpl.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-semibold">{tpl.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(tpl.status)}`}>{tpl.status}</span>
                </div>
                <div className="text-sm text-gray-600 mb-2">{tpl.subject}</div>
                <div className="text-xs text-gray-400 mb-4">Category: {tpl.category}</div>
                <div className="flex flex-wrap gap-1">
                  {tpl.variables.map(v => (
                    <span key={v} className="px-2 py-1 bg-gray-100 rounded text-xs">{`{{${v}}}`}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Audience</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opened</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicked</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {campaigns.map(camp => (
                  <tr key={camp.id}>
                    <td className="px-6 py-4 font-medium">{camp.name}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(camp.status)}`}>{camp.status}</span></td>
                    <td className="px-6 py-4">{camp.audience_size.toLocaleString()}</td>
                    <td className="px-6 py-4">{camp.sent.toLocaleString()}</td>
                    <td className="px-6 py-4">{camp.opened.toLocaleString()} ({camp.sent > 0 ? ((camp.opened/camp.sent)*100).toFixed(1) : 0}%)</td>
                    <td className="px-6 py-4">{camp.clicked.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
